<template>
  <section class="balance-import">
    <div class="header">
      <h2>Importação de saldo em massa</h2>
      <p>
        Faça download do template CSV, preencha com os ajustes de saldo (extra
        e/ou faltante) e envie. O upload gera um registro de auditoria
        (LogMasterUpdate) e fica disponível no histórico abaixo.
      </p>
    </div>

    <div class="box">
      <label class="box-title">1. Template CSV</label>
      <p class="hint">
        Formato: <code>cpf;data;extra;missing;observacao</code><br/>
        Horas no formato <code>HH:mm</code> (aceita acima de 24h, ex.: <code>120:30</code>,
        e também <code>HH:mm:ss</code>). Data em <code>dd/MM/yyyy</code>. O CPF pode vir
        com ou sem o zero à esquerda.
      </p>
      <button class="btn-default" @click="downloadTemplate">Baixar template</button>
    </div>

    <div class="box">
      <label class="box-title">2. Upload</label>
      <input type="file" accept=".csv" @change="onFileSelected" />
      <p v-if="selectedFile" class="file-info">
        Arquivo selecionado: <strong>{{ selectedFile.name }}</strong>
        ({{ Math.round(selectedFile.size / 1024) }} KB)
      </p>

      <button class="btn-primary" :disabled="!selectedFile || uploading" @click="upload">
        <span v-if="!uploading">Enviar</span>
        <span v-else>Enviando...</span>
      </button>

      <div v-if="lastResult" class="result-box">
        <strong>Resultado:</strong>
        <ul>
          <li>Total: {{ lastResult.totalRows }}</li>
          <li>Sucesso: {{ lastResult.successRows }}</li>
          <li>Erro: {{ lastResult.errorRows }}</li>
        </ul>

        <!-- Detalhe dos erros por linha (mostra o motivo exato de cada falha) -->
        <div v-if="resultErrors.length" class="result-errors">
          <strong>Linhas com erro:</strong>
          <ul>
            <li v-for="(e, i) in resultErrors" :key="i">
              Linha {{ errField(e, 'RowNumber') }}
              (CPF {{ errField(e, 'Cpf') }}, data {{ errField(e, 'DateStr') }}):
              {{ errField(e, 'Error') }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="box">
      <label class="box-title">3. Histórico de importações</label>
      <table class="table-history" v-if="history.length">
        <thead>
          <tr>
            <th>Data</th>
            <th>Master</th>
            <th>Arquivo</th>
            <th>Total</th>
            <th>Sucesso</th>
            <th>Erro</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in history" :key="h.id">
            <td>{{ formatDate(h.importedAt) }}</td>
            <td>{{ h.userName }}</td>
            <td><a :href="h.fileUrl" target="_blank">{{ h.fileName }}</a></td>
            <td>{{ h.totalRows }}</td>
            <td>{{ h.successRows }}</td>
            <td>{{ h.errorRows }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="empty">Nenhuma importação registrada ainda.</p>
    </div>
  </section>
</template>

<script>
import {
  BALANCE_IMPORT_TEMPLATE,
  BALANCE_IMPORT_UPLOAD,
  BALANCE_IMPORT_HISTORY
} from "../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import swal from "sweetalert";

export default {
  name: "balanceImport",
  data() {
    return {
      companyId: "",
      selectedFile: null,
      uploading: false,
      history: [],
      lastResult: null
    };
  },
  computed: {
    // A API serializa os erros a partir de tipos anônimos (PascalCase), mas
    // dependendo do formatter podem chegar em camelCase. Normalizamos a leitura.
    resultErrors() {
      if (!this.lastResult || !this.lastResult.errors) return [];
      return Array.isArray(this.lastResult.errors) ? this.lastResult.errors : [];
    }
  },
  beforeMount() {
    this.companyId = this.$route.params.idCompany;
    this.loadHistory();
  },
  methods: {
    downloadTemplate() {
      window.open(`${BALANCE_IMPORT_TEMPLATE}`, "_blank");
    },
    onFileSelected(event) {
      this.selectedFile = event.target.files[0] || null;
    },
    upload() {
      if (!this.selectedFile || this.uploading) return;
      this.uploading = true;

      const formData = new FormData();
      formData.append("companyId", this.companyId);
      formData.append("file", this.selectedFile);

      REQUEST_SERVICE.requestContext("POST", BALANCE_IMPORT_UPLOAD, this.$route.params.context, formData, true)
        .then((response) => {
          this.uploading = false;
          this.lastResult = response.data;
          const ok = response.data.successRows;
          const err = response.data.errorRows;
          if (err > 0)
            swal("Importação concluída", `${ok} sucesso(s), ${err} erro(s). Veja os detalhes abaixo.`, "warning");
          else
            swal("Sucesso", `Importação concluída: ${ok} sucesso(s).`, "success");
          this.loadHistory();
        })
        .catch((reason) => {
          this.uploading = false;
          const msg =
            (reason && reason.response && reason.response.data &&
              (reason.response.data.message ||
                (reason.response.data.erros &&
                  reason.response.data.erros[0] &&
                  reason.response.data.erros[0].value))) ||
            "Falha ao importar. Verifique o arquivo (formato/CPF) e tente novamente.";
          swal("Aviso", msg, "error");
        });
    },
    // Lê um campo do objeto de erro tolerando PascalCase ou camelCase.
    errField(e, name) {
      if (!e) return "";
      const lower = name.charAt(0).toLowerCase() + name.slice(1);
      const v = e[name] !== undefined ? e[name] : e[lower];
      return v === undefined || v === null ? "" : v;
    },
    loadHistory() {
      REQUEST_SERVICE.requestContext(
        "GET",
        `${BALANCE_IMPORT_HISTORY}?companyId=${this.companyId}`,
        this.$route.params.context
      ).then((response) => {
          this.history = Array.isArray(response.data) ? response.data : [];
        })
        .catch(() => { this.history = []; });
    },
    formatDate(isoDate) {
      if (!isoDate) return "";
      const d = new Date(isoDate);
      return d.toLocaleString("pt-BR");
    }
  }
};
</script>

<style lang="scss" scoped>
.balance-import {
  padding: 24px;
  .header { margin-bottom: 24px; h2 { font-weight: bold; margin-bottom: 8px; } p { color: #666; font-size: 13px; } }
  .box {
    background: #fff; border-radius: 6px; padding: 18px;
    margin-bottom: 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    .box-title { font-weight: 600; display: block; margin-bottom: 12px; }
    .hint { font-size: 12px; color: #777; margin-bottom: 12px; }
    .file-info { font-size: 13px; color: #444; margin: 8px 0; }
  }
  .btn-default { background: #8b8787; color: #fff; padding: 8px 20px; border-radius: 4px; border: none; cursor: pointer; }
  .btn-primary { background: #3598dc; color: #fff; padding: 8px 20px; border-radius: 4px; border: none; cursor: pointer; margin-left: 12px; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .result-box { margin-top: 14px; background: #f6f8fa; padding: 12px; border-radius: 4px; ul { margin: 6px 0 0 16px; } }
  .result-errors { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e3e6ea;
    strong { color: #c0392b; } ul { margin: 6px 0 0 16px; } li { color: #a94442; font-size: 12.5px; } }
  .table-history {
    width: 100%; border-collapse: collapse; font-size: 13px;
    th, td { padding: 8px; border-bottom: 1px solid #eee; text-align: left; }
    th { color: #555; font-weight: 600; background: #fafafa; }
    a { color: #3598dc; }
  }
  .empty { text-align: center; color: #999; padding: 20px 0; }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/companyDetail/balanceImport.vue